import React, { useState, useEffect, useCallback, useRef } from 'react';
import { withTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Alert,
  Chip,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  Autocomplete,
  CircularProgress,
  IconButton,
  InputAdornment,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import TireRepairIcon from '@mui/icons-material/TireRepair';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import axios from 'axios';
import { useUserStore } from 'store';
import { getCsrfToken } from '../../utils';
import SendIcon from '@mui/icons-material/Send';
import PrintIcon from '@mui/icons-material/Print';
import DecimalField from '../shared/DecimalField';
import ConditionReportDialog from './ConditionReportDialog';
import printTireLabel from './tireLabel';
import NeedsReplacementChip from './NeedsReplacementChip';
import TireHotelCardForm from './TireHotelCardForm';

const STEPS = ['swap_step_select_outgoing', 'swap_step_select_incoming', 'swap_step_inspect', 'swap_step_confirm'];

// The set coming off the car is most likely the other season from the one going on.
// All-season has no opposite, so the card form keeps its own default.
const OPPOSITE_SEASON = { winter: 'summer', summer: 'winter' };

// Error codes from the swap endpoint, shown translated
const SWAP_ERROR_KEYS = {
  incoming_card_not_found: 'tire_hotel.swap_error_incoming_card_not_found',
  location_not_found: 'tire_hotel.swap_error_location_not_found',
};

function TireSwapDialog({ t, open, onClose, vehicleId, vehicle, customer, maintenanceId, onSwapCompleted }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { currentOrganization } = useUserStore();

  const [activeStep, setActiveStep] = useState(0);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Step 0: select outgoing card (from storage -> onto vehicle)
  const [selectedOutgoingId, setSelectedOutgoingId] = useState(null);
  // Step 1: select incoming card (from vehicle -> into storage)
  const [selectedIncomingId, setSelectedIncomingId] = useState(null);
  const [skipIncoming, setSkipIncoming] = useState(false);
  // The set on the car may have no card yet: it is created here without leaving the swap
  const [newCardFormOpen, setNewCardFormOpen] = useState(false);

  // Step 2: inspection form
  const [incomingForm, setIncomingForm] = useState({
    tread_depth_fl: '',
    tread_depth_fr: '',
    tread_depth_rl: '',
    tread_depth_rr: '',
    condition_notes: '',
    storage_location_id: null,
  });
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [locationSearch, setLocationSearch] = useState('');
  const locationTimeoutRef = useRef(null);

  // What the org settings will do with this swap: the line it bills, the label orientation
  const [swapSettings, setSwapSettings] = useState(null);

  // Step 3: result
  const [swapping, setSwapping] = useState(false);
  const [swapResult, setSwapResult] = useState(null);
  const [conditionReportOpen, setConditionReportOpen] = useState(false);

  const storedCards = cards.filter((c) => c.status === 'stored');
  const onVehicleCards = cards.filter((c) => c.status === 'on_vehicle');
  const selectedOutgoing = cards.find((c) => c.id === selectedOutgoingId);
  const selectedIncoming = cards.find((c) => c.id === selectedIncomingId);

  const fetchCards = useCallback(async () => {
    if (!vehicleId || !currentOrganization?.organization_id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `/api/tire-hotel-cards/by_vehicle/?vehicle_id=${vehicleId}`,
        { headers: { 'X-Organization-Id': currentOrganization.organization_id } }
      );
      setCards(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to fetch tire hotel cards:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [vehicleId, currentOrganization?.organization_id]);

  // Fetch cards for the vehicle when dialog opens
  useEffect(() => {
    if (open) fetchCards();
  }, [open, fetchCards]);

  // A card made from step 1 is the set coming off the car: select it and show it in the list
  const handleNewCardSaved = (card) => {
    if (!card?.id) return;
    setSelectedIncomingId(card.id);
    setSkipIncoming(false);
    fetchCards();
  };

  // The confirm step tells the mechanic what lands on the työmääräys before they
  // commit to it, so the settings are fetched as soon as the dialog opens.
  useEffect(() => {
    if (!open || !currentOrganization?.organization_id) return;

    const fetchSwapSettings = async () => {
      try {
        const response = await axios.get('/api/tire-hotel-cards/swap-settings/', {
          headers: { 'X-Organization-Id': currentOrganization.organization_id },
        });
        setSwapSettings(response.data);
      } catch (err) {
        // A missing preview must not block the swap itself
        console.error('Failed to fetch tire swap settings:', err);
      }
    };
    fetchSwapSettings();
  }, [open, currentOrganization?.organization_id]);

  // Auto-select incoming card if exactly one on_vehicle card
  useEffect(() => {
    if (onVehicleCards.length === 1) {
      setSelectedIncomingId(onVehicleCards[0].id);
    }
  }, [cards]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch suggested locations when incoming card selected
  const fetchLocations = useCallback(async (card, search) => {
    if (!currentOrganization?.organization_id) return;
    setLocationsLoading(true);
    try {
      // The search goes to the server: suggest returns a capped slice, so filtering what came
      // back would hide every location that did not make the cut.
      const query = new URLSearchParams();
      // A staggered set sends both sizes; the wider one decides where it fits
      if (card?.tire_size) query.set('tire_size', card.tire_size);
      if (card?.tire_size_rear) query.set('tire_size_rear', card.tire_size_rear);
      if (search) query.set('search', search);
      const params = query.toString() ? `?${query.toString()}` : '';
      const response = await axios.get(`/api/storage-locations/suggest/${params}`, {
        headers: { 'X-Organization-Id': currentOrganization.organization_id },
      });
      setLocations(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error fetching locations:', err);
    } finally {
      setLocationsLoading(false);
    }
  }, [currentOrganization?.organization_id]);

  useEffect(() => {
    if (activeStep === 2 && selectedIncoming) {
      if (locationTimeoutRef.current) clearTimeout(locationTimeoutRef.current);
      locationTimeoutRef.current = setTimeout(() => {
        fetchLocations(selectedIncoming, locationSearch);
      }, 300);
      return () => {
        if (locationTimeoutRef.current) clearTimeout(locationTimeoutRef.current);
      };
    }
  }, [activeStep, selectedIncoming, locationSearch, fetchLocations]);

  // Pre-fill tread depths from incoming card, and storage location from outgoing card
  useEffect(() => {
    if (selectedIncoming) {
      setIncomingForm((prev) => ({
        ...prev,
        tread_depth_fl: selectedIncoming.tread_depth_fl != null ? String(selectedIncoming.tread_depth_fl) : '',
        tread_depth_fr: selectedIncoming.tread_depth_fr != null ? String(selectedIncoming.tread_depth_fr) : '',
        tread_depth_rl: selectedIncoming.tread_depth_rl != null ? String(selectedIncoming.tread_depth_rl) : '',
        tread_depth_rr: selectedIncoming.tread_depth_rr != null ? String(selectedIncoming.tread_depth_rr) : '',
        storage_location_id: selectedOutgoing?.storage_location || null,
      }));
      // Pre-fill location dropdown with the outgoing card's slot
      if (selectedOutgoing?.storage_location && selectedOutgoing?.location_label) {
        setSelectedLocation({ id: selectedOutgoing.storage_location, label: selectedOutgoing.location_label });
      }
    }
  }, [selectedIncoming, selectedOutgoing]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setActiveStep(0);
      setCards([]);
      setSelectedOutgoingId(null);
      setSelectedIncomingId(null);
      setSkipIncoming(false);
      setNewCardFormOpen(false);
      setIncomingForm({
        tread_depth_fl: '',
        tread_depth_fr: '',
        tread_depth_rl: '',
        tread_depth_rr: '',
        condition_notes: '',
        storage_location_id: null,
      });
      setLocations([]);
      setSelectedLocation(null);
      setLocationSearch('');
      setSwapSettings(null);
      setSwapping(false);
      setSwapResult(null);
      setError(null);
    }
  }, [open]);

  const handleDecimalChange = (field) => (value) => {
    setIncomingForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleNext = () => {
    if (activeStep === 1 && (skipIncoming || !selectedIncomingId)) {
      // Skip inspection step if no incoming card
      setActiveStep(3);
    } else if (activeStep === 3) {
      handleSwap();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (activeStep === 3 && (skipIncoming || !selectedIncomingId)) {
      // Go back to step 1 if we skipped inspection
      setActiveStep(1);
    } else {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleSwap = async () => {
    if (!selectedOutgoingId) return;
    setSwapping(true);
    setError(null);

    const payload = {
      maintenance_id: maintenanceId || null,
    };

    if (selectedIncomingId && !skipIncoming) {
      payload.incoming_card_id = selectedIncomingId;
      if (incomingForm.tread_depth_fl) payload.incoming_tread_depth_fl = incomingForm.tread_depth_fl;
      if (incomingForm.tread_depth_fr) payload.incoming_tread_depth_fr = incomingForm.tread_depth_fr;
      if (incomingForm.tread_depth_rl) payload.incoming_tread_depth_rl = incomingForm.tread_depth_rl;
      if (incomingForm.tread_depth_rr) payload.incoming_tread_depth_rr = incomingForm.tread_depth_rr;
      payload.incoming_condition_notes = incomingForm.condition_notes || '';
      if (incomingForm.storage_location_id) {
        payload.incoming_storage_location_id = incomingForm.storage_location_id;
      }
    }

    try {
      const response = await axios.post(
        `/api/tire-hotel-cards/${selectedOutgoingId}/swap/`,
        payload,
        {
          headers: {
            'X-Organization-Id': currentOrganization.organization_id,
            'X-CSRFToken': getCsrfToken(),
          },
        }
      );
      setSwapResult(response.data);
    } catch (err) {
      console.error('Swap failed:', err);
      const code = err.response?.data?.error;
      setError(SWAP_ERROR_KEYS[code] ? t(SWAP_ERROR_KEYS[code]) : (code || err.message));
    } finally {
      setSwapping(false);
    }
  };

  const canProceed = () => {
    switch (activeStep) {
      case 0:
        return !!selectedOutgoingId;
      case 1:
        return skipIncoming || !!selectedIncomingId;
      case 2:
        return true; // Inspection fields are optional
      case 3:
        return !swapping && !swapResult;
      default:
        return false;
    }
  };

  const getStepLabel = (index) => t(`tire_hotel.${STEPS[index]}`);

  const renderCardRow = (card, isSelected, onSelect) => (
    <Box
      key={card.id}
      onClick={() => onSelect(card.id)}
      sx={{
        p: 2,
        border: '2px solid',
        borderColor: isSelected ? 'primary.main' : 'divider',
        borderRadius: '10px',
        cursor: 'pointer',
        bgcolor: isSelected ? 'primary.50' : 'background.paper',
        '&:hover': { borderColor: isSelected ? 'primary.main' : 'text.secondary' },
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { sm: 'center' },
        gap: 1.5,
      }}
    >
      <Radio checked={isSelected} size="small" sx={{ p: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Chip
            label={t(`tire_hotel.${card.tire_set_type}`)}
            size="small"
            color={card.tire_set_type === 'winter' ? 'info' : card.tire_set_type === 'summer' ? 'warning' : 'default'}
          />
          {card.studded && <Chip label={t('tire_hotel.studded')} size="small" variant="outlined" />}
          <Typography variant="body2" fontWeight={600}>
            {card.tire_brand} {card.tire_size_display}
          </Typography>
          <NeedsReplacementChip card={card} t={t} />
        </Box>
        {/* The sales moment: the customer is at the counter, so say why the set should go */}
        {card.needs_replacement && (
          <Typography variant="caption" sx={{ color: 'warning.dark', display: 'block', mt: 0.5 }}>
            {t('tire_hotel.needs_replacement_hint')}{card.replacement_reason ? `: ${card.replacement_reason}` : ''}
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
          {card.location_label && (
            <Typography variant="caption" color="text.secondary">
              {t('tire_hotel.location')}: {card.location_label}
            </Typography>
          )}
          {card.min_tread_depth && (
            <Typography
              variant="caption"
              sx={{
                color: parseFloat(card.min_tread_depth) < 3
                  ? 'error.main'
                  : parseFloat(card.min_tread_depth) < 4
                    ? 'warning.main'
                    : 'success.main',
                fontWeight: 600,
              }}
            >
              {t('tire_hotel.min_tread_depth')}: {card.min_tread_depth} mm
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );

  const renderStep0 = () => (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('tire_hotel.swap_select_outgoing_help')}
      </Typography>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : storedCards.length === 0 ? (
        <Alert severity="info">{t('tire_hotel.swap_no_stored_cards')}</Alert>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {storedCards.map((card) =>
            renderCardRow(card, selectedOutgoingId === card.id, setSelectedOutgoingId)
          )}
        </Box>
      )}
    </Box>
  );

  const renderStep1 = () => (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('tire_hotel.swap_select_incoming_help')}
      </Typography>
      {onVehicleCards.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('tire_hotel.swap_no_on_vehicle_cards')}
        </Alert>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
          {onVehicleCards.map((card) =>
            renderCardRow(card, selectedIncomingId === card.id && !skipIncoming, (id) => {
              setSelectedIncomingId(id);
              setSkipIncoming(false);
            })
          )}
        </Box>
      )}
      {vehicle?.id && (
        <Box sx={{ mb: 1.5 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setNewCardFormOpen(true)}
            sx={{ textTransform: 'none' }}
            data-testid="tire-swap-create-card"
          >
            {t('tire_hotel.swap_new_card_for_incoming')}
          </Button>
        </Box>
      )}
      <FormControlLabel
        control={
          <Radio
            checked={skipIncoming}
            onChange={() => {
              setSkipIncoming(true);
              setSelectedIncomingId(null);
            }}
            size="small"
          />
        }
        label={
          <Typography variant="body2" color="text.secondary">
            {t('tire_hotel.swap_skip_incoming')}
          </Typography>
        }
      />
    </Box>
  );

  const renderStep2 = () => (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('tire_hotel.swap_inspect_help')}
      </Typography>

      {/* Tread depths */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
        {t('tire_hotel.tread_depth')}
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, maxWidth: 400, mb: 3 }}>
        <DecimalField
          label={t('tire_hotel.tread_depth_fl')}
          value={incomingForm.tread_depth_fl}
          onChange={handleDecimalChange('tread_depth_fl')}
          maxDecimals={1}
          size="small"
          InputProps={{ endAdornment: <InputAdornment position="end">mm</InputAdornment> }}
        />
        <DecimalField
          label={t('tire_hotel.tread_depth_fr')}
          value={incomingForm.tread_depth_fr}
          onChange={handleDecimalChange('tread_depth_fr')}
          maxDecimals={1}
          size="small"
          InputProps={{ endAdornment: <InputAdornment position="end">mm</InputAdornment> }}
        />
        <DecimalField
          label={t('tire_hotel.tread_depth_rl')}
          value={incomingForm.tread_depth_rl}
          onChange={handleDecimalChange('tread_depth_rl')}
          maxDecimals={1}
          size="small"
          InputProps={{ endAdornment: <InputAdornment position="end">mm</InputAdornment> }}
        />
        <DecimalField
          label={t('tire_hotel.tread_depth_rr')}
          value={incomingForm.tread_depth_rr}
          onChange={handleDecimalChange('tread_depth_rr')}
          maxDecimals={1}
          size="small"
          InputProps={{ endAdornment: <InputAdornment position="end">mm</InputAdornment> }}
        />
      </Box>

      {/* Condition notes */}
      <TextField
        label={t('tire_hotel.condition_notes')}
        value={incomingForm.condition_notes}
        onChange={(e) => setIncomingForm((prev) => ({ ...prev, condition_notes: e.target.value }))}
        multiline
        rows={2}
        fullWidth
        size="small"
        sx={{ mb: 3 }}
      />

      {/* Storage location */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        {t('tire_hotel.location')}
      </Typography>
      <Autocomplete
        options={locations}
        getOptionLabel={(option) => option?.label || ''}
        loading={locationsLoading}
        value={selectedLocation}
        onChange={(e, value) => {
          setSelectedLocation(value);
          setIncomingForm((prev) => ({ ...prev, storage_location_id: value?.id || null }));
        }}
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
        filterOptions={(x) => x}
        onInputChange={(e, value, reason) => {
          if (reason === 'input') setLocationSearch(value);
          if (reason === 'clear') setLocationSearch('');
        }}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <span>{option.label}</span>
              {option.fits_size === false && (
                <Typography variant="caption" color="warning.main" sx={{ ml: 'auto' }}>
                  {t('tire_hotel.size_may_not_fit')}
                </Typography>
              )}
            </Box>
          </li>
        )}
        noOptionsText={t('tire_hotel.no_locations_found')}
        renderInput={(params) => (
          <TextField
            {...params}
            autoComplete="off"
            label={t('tire_hotel.select_location')}
            size="small"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {locationsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        sx={{ maxWidth: 400 }}
      />
    </Box>
  );

  const renderBillingPreview = () => {
    const billing = swapSettings?.billing;
    if (!maintenanceId || !billing) return null;

    // No price here on purpose: it depends on the customer's jobs discount and the
    // VAT entry mode, and a wrong number on the confirm screen is worse than none.
    return (
      <Box
        sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: '10px', mb: 2 }}
        data-testid="swap-billing-preview"
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: billing.mode === 'none' ? 0 : 1 }}>
          <ReceiptLongIcon fontSize="small" color="action" />
          <Typography variant="subtitle2" color="text.secondary">
            {t('tire_hotel.swap_billing_preview_title')}
          </Typography>
        </Box>
        {billing.mode === 'none' ? (
          <Typography variant="body2" color="text.secondary">
            {t('tire_hotel.swap_billing_preview_none')}
          </Typography>
        ) : (
          <>
            <Typography variant="body2">
              {billing.mode === 'package'
                ? t('tire_hotel.swap_billing_preview_package', { name: billing.name })
                : t('tire_hotel.swap_billing_preview_job', { name: billing.name })}
            </Typography>
            {billing.mode === 'package' && (
              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                <Chip size="small" variant="outlined" label={`${billing.job_count} ${t('huoltopaketit.jobsShort')}`} />
                <Chip size="small" variant="outlined" label={`${billing.part_count} ${t('huoltopaketit.partsShort')}`} />
              </Box>
            )}
          </>
        )}
        {billing.fallback && (
          <Alert severity="warning" sx={{ mt: 1.5 }}>
            {t('tire_hotel.swap_billing_preview_fallback')}
          </Alert>
        )}
      </Box>
    );
  };

  const renderStep3 = () => {
    if (swapResult) {
      return (
        <Box data-testid="tire-swap-success">
          <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
            {t('tire_hotel.swap_completed')}
          </Alert>
          {swapResult.flagged && (
            <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
              {t('tire_hotel.swap_flagged_warning')}
              {swapResult.incoming_card?.replacement_reason ? ` (${swapResult.incoming_card.replacement_reason})` : ''}
            </Alert>
          )}
          {swapResult.incoming_card && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={() => printTireLabel(
                  swapResult.incoming_card,
                  currentOrganization,
                  t,
                  swapSettings?.label_orientation !== 'portrait',
                )}
                data-testid="tire-swap-print-label"
              >
                {t('tire_hotel.print_storage_label')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<SendIcon />}
                onClick={() => setConditionReportOpen(true)}
              >
                {t('tire_hotel.send_condition_report')}
              </Button>
            </Box>
          )}
          <ConditionReportDialog
            open={conditionReportOpen}
            onClose={() => setConditionReportOpen(false)}
            card={swapResult.incoming_card || null}
          />
        </Box>
      );
    }

    return (
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('tire_hotel.swap_confirm_help')}
        </Typography>

        {/* Outgoing card summary */}
        {selectedOutgoing && (
          <Box sx={{ p: 2, border: '1px solid', borderColor: 'success.light', borderRadius: '10px', mb: 2, bgcolor: 'success.50' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <ArrowForwardIcon color="success" fontSize="small" />
              <Typography variant="subtitle2" color="success.dark">
                {t('tire_hotel.swap_going_to_vehicle')}
              </Typography>
            </Box>
            <Typography variant="body2">
              {selectedOutgoing.tire_brand} {selectedOutgoing.tire_size_display}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
              <Chip
                label={t(`tire_hotel.${selectedOutgoing.tire_set_type}`)}
                size="small"
                color={selectedOutgoing.tire_set_type === 'winter' ? 'info' : 'warning'}
              />
              {selectedOutgoing.location_label && (
                <Typography variant="caption" color="text.secondary">
                  {t('tire_hotel.location')}: {selectedOutgoing.location_label}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* Incoming card summary */}
        {selectedIncoming && !skipIncoming && (
          <Box sx={{ p: 2, border: '1px solid', borderColor: 'info.light', borderRadius: '10px', mb: 2, bgcolor: 'info.50' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <ArrowForwardIcon color="info" fontSize="small" sx={{ transform: 'rotate(180deg)' }} />
              <Typography variant="subtitle2" color="info.dark">
                {t('tire_hotel.swap_going_to_storage')}
              </Typography>
            </Box>
            <Typography variant="body2">
              {selectedIncoming.tire_brand} {selectedIncoming.tire_size_display}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
              <Chip
                label={t(`tire_hotel.${selectedIncoming.tire_set_type}`)}
                size="small"
                color={selectedIncoming.tire_set_type === 'winter' ? 'info' : 'warning'}
              />
              {selectedLocation && (
                <Typography variant="caption" color="text.secondary">
                  {t('tire_hotel.location')}: {selectedLocation.label}
                </Typography>
              )}
            </Box>
            {(incomingForm.tread_depth_fl || incomingForm.tread_depth_fr || incomingForm.tread_depth_rl || incomingForm.tread_depth_rr) && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {t('tire_hotel.tread_depth')}: {incomingForm.tread_depth_fl || '-'} / {incomingForm.tread_depth_fr || '-'} / {incomingForm.tread_depth_rl || '-'} / {incomingForm.tread_depth_rr || '-'} mm
              </Typography>
            )}
            {incomingForm.condition_notes && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {t('tire_hotel.condition_notes')}: {incomingForm.condition_notes}
              </Typography>
            )}
          </Box>
        )}

        {renderBillingPreview()}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Box>
    );
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0: return renderStep0();
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      default: return null;
    }
  };

  const getNextButtonLabel = () => {
    if (activeStep === 3) {
      if (swapResult) return null; // Hide next button after success
      return t('tire_hotel.confirm_swap');
    }
    return t('common.next');
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={swapping ? undefined : onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      data-testid="tire-swap-dialog"
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TireRepairIcon />
        {t('tire_hotel.swap_title')}
        <Box sx={{ flex: 1 }} />
        <IconButton onClick={onClose} disabled={swapping} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stepper
          activeStep={activeStep}
          alternativeLabel={isMobile}
          sx={{ mb: 3 }}
        >
          {STEPS.map((step, idx) => (
            <Step key={step} completed={idx < activeStep}>
              <StepLabel>{!isMobile ? getStepLabel(idx) : ''}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {renderStepContent()}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        {swapResult ? (
          <Button
            variant="contained"
            onClick={() => {
              if (onSwapCompleted) onSwapCompleted();
              onClose();
            }}
          >
            {t('Close')}
          </Button>
        ) : (
          <>
            {activeStep > 0 && (
              <Button onClick={handleBack} disabled={swapping}>
                {t('Back')}
              </Button>
            )}
            <Box sx={{ flex: 1 }} />
            {getNextButtonLabel() && (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!canProceed() || swapping}
                startIcon={swapping ? <CircularProgress size={16} /> : null}
                data-testid={activeStep === 3 ? 'tire-swap-confirm' : 'tire-swap-next'}
              >
                {getNextButtonLabel()}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
    <TireHotelCardForm
      t={t}
      open={open && newCardFormOpen}
      onClose={() => setNewCardFormOpen(false)}
      onSaved={handleNewCardSaved}
      prefill={{
        customer: customer || null,
        vehicle: vehicle || null,
        tire_set_type: OPPOSITE_SEASON[selectedOutgoing?.tire_set_type],
        status: 'on_vehicle',
        lockVehicle: true,
      }}
    />
    </>
  );
}

export default withTranslation()(TireSwapDialog);
